import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import {
  useActiveContributors,
  useActiveRepos,
  useCommitHistory,
  useContributorStats,
  useDashboardStats,
  useMonthlyCommits,
} from "../../lib/githubHooks";
import { Alert, Container, Typography, useMediaQuery } from "@mui/material";
import { formatDistance } from "date-fns";

import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import StatsCard from "../../components/github/StatsCard";
import LoadingIndicator from "../../components/github/loader";
import CardComponent from "../../components/github/CardComponent";
import ChartCard from "../../components/github/ChartCard";
import Footer from "../../components/common/Footer";
import Header from "../../components/common/Header";
import Navbar from "../../components/common/NavBar";

const processChartData = (
  chartData: Record<string, number>,
  labelName: string,
) => {
  const currentMonth = new Date().getMonth();
  const dateLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthLabels = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (currentMonth + i + 1) % 12;
    return dateLabels[monthIndex];
  });

  const months = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = ((currentMonth + i + 1) % 12) + 1;
    return chartData[monthIndex.toString()] || 0;
  });

  return {
    labels: monthLabels,
    datasets: [
      {
        label: labelName,
        data: months,
        fill: false,
        borderColor: "rgb(159, 204, 46)",
        backgroundColor: "rgb(159, 204, 46)",
        barPercentage: 0.4,
        borderRadius: 10,
      },
    ],
  };
};

interface DashboardProps {
  type?: string;
}

const Dashboard = ({ type = "cardano" }: DashboardProps) => {
  const { data, loading } = useDashboardStats(type);

  const { contributorsData, contributorsLoading, contributorsError } =
    useContributorStats(type);

  const {
    chartData,
    chartLoading,
    error: chartError,
  } = useMonthlyCommits(type);

  const processedChartDataBar = processChartData(
    chartData?.monthly_commits ?? {},
    "Commits",
  );

  const {
    commits,
    commitsLoading,
    error: commitsError,
  } = useCommitHistory(type);
  const {
    activeRepoData,
    activeRepoLoading,
    error: activeRepoError,
  } = useActiveRepos(type);

  const processedChartDataLine = processChartData(
    activeRepoData?.monthly_active_repos ?? {},
    "Active Repositories",
  );

  const {
    ActiveContributorsData,
    ActiveContributorsLoading,
    error: activeContributorsError,
  } = useActiveContributors(type);
  document.title = `${type.charAt(0).toUpperCase() + type.slice(1)} Github Dashboard`;

  const isBelow800 = useMediaQuery("(max-width:800px)");

  const date = new Date();

  const now_utc = date.toISOString().slice(0, -5);

  const infoDetails = {
    commitsChartDescription: {
      desc: "Number of monthly commits in the last 12 months. Updated weekly",
      time:
        "Last updated " +
        formatDistance(chartData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    activeReposChartDescription: {
      desc: "A repository is considered active if it has at least 1 commit in that particular month. Updated Weekly",
      time:
        "Last updated " +
        formatDistance(activeRepoData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    activeContributorsDescription: {
      desc: "Number of active contributors in the last 30 days. Updated hourly",
      time:
        "Last updated " +
        formatDistance(ActiveContributorsData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    recentCommitsDescription: {
      desc: "Top 500 most recent commits.",
      time:
        "Last updated " +
        formatDistance(commits?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    topContributorsDescription: {
      desc: "Top 10 contributors of the last 30 days ranked by commits.",
      time:
        "Last updated " +
        formatDistance(contributorsData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    statDescription: {
      desc: "",
      time:
        "Last updated " +
        formatDistance(data?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
  };

  return (
    <div>
      <Navbar lightTheme />
      <Container maxWidth={"xl"} sx={{ p: "5px" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "600", pt: 2, pb: -3 }}
          textAlign={isBelow800 ? "center" : "left"}
        >
          Github Contribution ({type.charAt(0).toUpperCase() + type.slice(1)})
        </Typography>
        <Grid container spacing={{ xs: 0, sm: 2 }} sx={{ mt: 0 }}>
          <Grid
            item
            xs={12}
            sm={5}
            md={5}
            lg={4}
            xl={3}
            sx={{ background: "#F8F8F8", mt: 2, borderRadius: "15px" }}
          >
            <Grid container spacing={2} sx={{ p: 1 }}>
              <Grid item xs={6}>
                <StatsCard
                  title="Total Repositories"
                  value={loading ? "..." : (data?.total_repos ?? "N/A")}
                  change={loading ? "" : `${data?.new_repos_last_week}`}
                />
              </Grid>
              <Grid item xs={6}>
                <StatsCard
                  title="Total Contributors"
                  value={loading ? "..." : (data?.total_contributors ?? "N/A")}
                  change={loading ? "" : `${data?.new_contributors_last_week}`}
                />
              </Grid>
              <Grid item xs={6}>
                <StatsCard
                  title="Total PRs"
                  value={loading ? "..." : (data?.total_prs ?? "N/A")}
                  change={loading ? "" : `${data?.new_prs_last_week}`}
                />
              </Grid>
              <Grid item xs={6}>
                <StatsCard
                  title="Total Commits"
                  value={loading ? "..." : (data?.total_commits ?? "N/A")}
                  change={loading ? "" : `${data?.new_commits_last_week}`}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12} sm={12} md={12} sx={{ background: "#F8F8F8" }}>
                {contributorsLoading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    {contributorsError ? (
                      <div className="alertContainer">
                        <Alert severity="error">
                          {JSON.stringify(contributorsError)}
                        </Alert>
                      </div>
                    ) : (
                      <CardComponent
                        title="Top Contributors"
                        data={contributorsData?.top_contributors || {}}
                        type="List"
                        isBelow800={isBelow800}
                        info={infoDetails.topContributorsDescription}
                      />
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            sm={7}
            md={7}
            lg={8}
            xl={9}
            container
            spacing={2}
            sx={{ pt: 0 }}
          >
            <Grid item xs={12} sm={12} md={12}>
              {activeRepoLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {activeRepoError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(activeRepoError)}
                      </Alert>
                    </div>
                  ) : (
                    <ChartCard
                      title="Active Repositories"
                      data={processedChartDataLine}
                      type="Line"
                      isBelow800={isBelow800}
                      info={infoDetails.activeReposChartDescription}
                    />
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {chartLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {chartError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(chartError)}
                      </Alert>
                    </div>
                  ) : (
                    <ChartCard
                      title="Commits"
                      data={processedChartDataBar}
                      type="Bar"
                      isBelow800={isBelow800}
                      info={infoDetails.commitsChartDescription}
                    />
                  )}
                </>
              )}
            </Grid>
            {/* <Grid item xs={12} sm={12} md={12}>
              {ActiveContributorsLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {activeContributorsError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(activeContributorsError)}
                      </Alert>
                    </div>
                  ) : (
                    <CardComponent
                      title="Contributors"
                      data={ActiveContributorsData?.active_contributors || {}}
                      type="Ring"
                      isBelow800={isBelow800}
                      info={infoDetails.activeContributorsDescription}
                    />
                  )}
                </>
              )}
            </Grid> */}
          </Grid>
        </Grid>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Box sx={{ mx: "auto" }}>
            <Grid container spacing={10} mb={5}>
              <Grid item xs={12}>
                {commitsLoading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    {commitsError ? (
                      <div className="alertContainer">
                        <Alert severity="error">
                          {JSON.stringify(commitsError)}
                        </Alert>
                      </div>
                    ) : (
                      <CardComponent
                        title="Recent Commits"
                        data={commits?.recent_commits || {}}
                        type="ListCommits"
                        isBelow800={isBelow800}
                        info={infoDetails.recentCommitsDescription}
                      />
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
          <div style={{ width: "100%", height: "20px" }}></div>
        </Box>
      </Container>

      <Footer lightMode />
    </div>
  );
};

export default Dashboard;
